import { useEffect, useState } from "react";
import {
  createPurchaseOrder,
  listInventoryItems,
  listPurchaseOrders,
  listSuppliers,
  listWarehouses,
  receivePurchaseOrder,
  type AdminSession,
  type InventoryItemOption,
  type PurchaseOrderItem,
  type SupplierItem,
  type WarehouseItem,
} from "@/lib/api";

type DocType = "oc" | "factura" | "guia";

type OrderLine = {
  inventoryItemId: string;
  quantity: number;
  unitCost: number;
};

type OrderHeaderForm = {
  supplierId: string;
  docType: DocType;
  docNumber: string;
  docDate: string;
};

const emptyLine: OrderLine = { inventoryItemId: "", quantity: 1, unitCost: 0 };

function emptyHeaderForm(): OrderHeaderForm {
  return {
    supplierId: "",
    docType: "factura",
    docNumber: "",
    docDate: new Date().toISOString().slice(0, 10),
  };
}

function docTypeLabel(value: string): string {
  if (value === "oc") return "Orden de compra";
  if (value === "factura") return "Factura";
  if (value === "guia") return "Guía de despacho";
  return value;
}

export default function ComprasSection({ session }: { session: AdminSession }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const [suppliers, setSuppliers] = useState<SupplierItem[]>([]);
  const [items, setItems] = useState<InventoryItemOption[]>([]);
  const [warehouses, setWarehouses] = useState<WarehouseItem[]>([]);
  const [orders, setOrders] = useState<PurchaseOrderItem[]>([]);
  const [receiveWarehouseId, setReceiveWarehouseId] = useState("");

  const [header, setHeader] = useState<OrderHeaderForm>(emptyHeaderForm());
  const [lines, setLines] = useState<OrderLine[]>([{ ...emptyLine }]);

  async function load(currentSession: AdminSession): Promise<void> {
    setLoading(true);
    setError("");
    try {
      const [supplierData, itemData, warehouseData, orderData] = await Promise.all([
        listSuppliers(currentSession),
        listInventoryItems(currentSession),
        listWarehouses(currentSession),
        listPurchaseOrders(currentSession),
      ]);
      setSuppliers(supplierData);
      setItems(itemData);
      setWarehouses(warehouseData);
      setOrders(orderData);
      setHeader((prev) => ({ ...prev, supplierId: prev.supplierId || supplierData[0]?.id || "" }));
      setReceiveWarehouseId((prev) => prev || warehouseData[0]?.id || "");
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudieron cargar compras");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(session);
  }, [session]);

  function updateLine(index: number, patch: Partial<OrderLine>): void {
    setLines((prev) => prev.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  }

  function addLine(): void {
    setLines((prev) => [...prev, { ...emptyLine }]);
  }

  function removeLine(index: number): void {
    setLines((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  }

  const total = lines.reduce((acc, line) => acc + Number(line.quantity || 0) * Number(line.unitCost || 0), 0);

  async function handleCreateOrder(): Promise<void> {
    if (!header.supplierId || !header.docNumber.trim() || !header.docDate) {
      setError("Completa proveedor, número de documento y fecha.");
      return;
    }
    const validLines = lines.filter((line) => line.inventoryItemId && line.quantity > 0 && line.unitCost > 0);
    if (validLines.length === 0) {
      setError("Agrega al menos un ítem con cantidad y valor de compra.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await createPurchaseOrder(session, {
        supplierId: header.supplierId,
        docType: header.docType,
        docNumber: header.docNumber.trim(),
        docDate: header.docDate,
        items: validLines.map((line) => ({
          inventoryItemId: line.inventoryItemId,
          quantity: Number(line.quantity),
          unitCost: Number(line.unitCost),
        })),
      });
      setHeader((prev) => ({ ...emptyHeaderForm(), supplierId: prev.supplierId }));
      setLines([{ ...emptyLine }]);
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo crear la compra");
    } finally {
      setSaving(false);
    }
  }

  async function handleReceiveOrder(orderId: string): Promise<void> {
    if (!receiveWarehouseId) {
      setError("Selecciona una bodega para recepción.");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await receivePurchaseOrder(session, orderId, { warehouseId: receiveWarehouseId });
      await load(session);
    } catch (e) {
      setError(e instanceof Error ? e.message : "No se pudo recepcionar la compra");
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
      {error ? (
        <div className="lg:col-span-3 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">{error}</div>
      ) : null}

      <div className="lg:col-span-1">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <h2 className="mb-2 text-sm font-semibold text-gray-700">Nueva Compra</h2>
          <div className="space-y-2">
            <select value={header.supplierId} onChange={(e) => setHeader((p) => ({ ...p, supplierId: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="">Proveedor</option>
              {suppliers.map((supplier) => <option key={supplier.id} value={supplier.id}>{supplier.name}</option>)}
            </select>
            <select value={header.docType} onChange={(e) => setHeader((p) => ({ ...p, docType: e.target.value as DocType }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm">
              <option value="oc">Orden de compra</option>
              <option value="factura">Factura</option>
              <option value="guia">Guía de despacho</option>
            </select>
            <input value={header.docNumber} onChange={(e) => setHeader((p) => ({ ...p, docNumber: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" placeholder="N° documento" />
            <input type="date" value={header.docDate} onChange={(e) => setHeader((p) => ({ ...p, docDate: e.target.value }))} className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm" />
          </div>

          <div className="mt-4">
            <div className="mb-1 flex items-center justify-between">
              <span className="text-xs font-semibold uppercase text-gray-500">Detalle</span>
              <button onClick={addLine} className="rounded-lg border border-blue-200 px-2 py-1 text-xs text-blue-700">+ Agregar ítem</button>
            </div>
            <div className="space-y-2">
              {lines.map((line, index) => (
                <div key={index} className="rounded-lg border border-gray-100 p-2">
                  <select value={line.inventoryItemId} onChange={(e) => updateLine(index, { inventoryItemId: e.target.value })} className="mb-2 w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs">
                    <option value="">Producto</option>
                    {items.map((item) => <option key={item.id} value={item.id}>{item.sku} · {item.name}</option>)}
                  </select>
                  <div className="flex items-center gap-2">
                    <input type="number" min={1} value={line.quantity} onChange={(e) => updateLine(index, { quantity: Number(e.target.value || 1) })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs" placeholder="Cantidad" />
                    <input type="number" min={0} value={line.unitCost} onChange={(e) => updateLine(index, { unitCost: Number(e.target.value || 0) })} className="w-full rounded-lg border border-gray-200 px-2 py-1.5 text-xs" placeholder="Valor compra (bruto)" />
                    <button onClick={() => removeLine(index)} disabled={lines.length === 1} className="shrink-0 rounded-lg border border-red-200 px-2 py-1.5 text-xs text-red-700 disabled:opacity-40">✕</button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between">
            <span className="text-sm text-gray-600">Total: <strong>${Math.round(total).toLocaleString("es-CL")}</strong></span>
            <button disabled={saving} onClick={() => void handleCreateOrder()} className="rounded-lg bg-blue-600 px-3 py-2 text-sm text-white">Crear compra</button>
          </div>
        </div>
      </div>

      <div className="lg:col-span-2">
        <div className="rounded-xl border border-gray-100 bg-white p-4">
          <div className="mb-2 flex items-center justify-between">
            <h2 className="text-sm font-semibold text-gray-700">Compras registradas</h2>
            <div className="flex items-center gap-2">
              <select value={receiveWarehouseId} onChange={(e) => setReceiveWarehouseId(e.target.value)} className="rounded-lg border border-gray-200 px-2 py-1 text-xs">
                {warehouses.map((warehouse) => <option key={warehouse.id} value={warehouse.id}>{warehouse.code}</option>)}
              </select>
              <span className="text-xs text-gray-500">Bodega recepción</span>
            </div>
          </div>
          {loading ? (
            <div className="text-sm text-gray-500">Cargando compras...</div>
          ) : (
            <div className="space-y-2">
              {orders.map((order) => (
                <div key={order.id} className="rounded-lg border border-gray-100 p-3">
                  <div className="flex items-center justify-between">
                    <div>
                      <div className="text-sm font-semibold text-gray-800">
                        {docTypeLabel(order.docType)} {order.docNumber ? `N° ${order.docNumber}` : ""}
                      </div>
                      <div className="text-xs text-gray-500">
                        {order.supplierName} · {order.docDate ? new Date(order.docDate).toLocaleDateString("es-CL") : "-"}
                      </div>
                    </div>
                    <div className="text-right">
                      <div className="text-sm font-semibold text-gray-800">${Math.round(order.totalAmount).toLocaleString("es-CL")}</div>
                      <div className="text-xs text-gray-500">{order.status}</div>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    {order.items.map((item) => `${item.itemName} (${item.quantity} x $${Math.round(item.unitCost).toLocaleString("es-CL")})`).join(" | ")}
                  </div>
                  {order.status !== "received" ? (
                    <div className="mt-2 flex justify-end">
                      <button disabled={saving} onClick={() => void handleReceiveOrder(order.id)} className="rounded-lg bg-emerald-600 px-3 py-1.5 text-xs text-white">Recepcionar</button>
                    </div>
                  ) : null}
                </div>
              ))}
              {orders.length === 0 ? <div className="text-sm text-gray-500">No hay compras registradas.</div> : null}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
